import React, {useContext, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Box, Button, Dialog, DialogActions, DialogContent, Grid, Paper, Typography, useTheme, MenuItem, IconButton, Menu } from "@mui/material";
import {useTranslation} from "react-i18next";
import {useMutation} from "@tanstack/react-query";
import DialogTitle from "@mui/material/DialogTitle";
import SignatureCanvas from "react-signature-canvas";
import {useRecoilValue} from "recoil";
import DialogContentText from "@mui/material/DialogContentText";
import dayjs from "dayjs";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import FileUploader from "../../../../util/component/FileUploader";
import CheckStepper from "../check-stepper/CheckStepper";
import {checkEquipmentsState, checkOutInModeState, checkVehicleStatusState, equipmentsEventState,
	eventsTypeState, raFetchedDataState, summaryState} from "../state";
import SignStep from "../../digital-sign/component/SignStep";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {
	// fetchCheckInPayment,
	fetchCheckOutInAsyncConfirmForm,
	recoveryCheckInUploadDoc,
	fetchRaUpdateCheckInOut,
	getCustomerBaseById
} from "../services/http.services";
import {raPreViewDownloadPdf} from "../../create-ra/services/http.services";
import CustomLoader from "../../../../util/component/CustomLoader";
import {DOWNLOAD_PDF_TYPE} from "../../create-ra/preview-and-signing/config";
import {clearCheckOutInSessionStorage} from "../services/sessionStorageManager";
import {IDamage, IEvent} from "../../models/models";
import {deepClone} from "../../../../util/state/commonFunctions";
import {getBillingCustomerId, isEffectiveDateChanged} from "../services/utils";
import {recoveryRaModeState} from "../../create-ra/state";
import {GB, IT, FR, ES, PT} from "country-flag-icons/react/1x1";
import { TryRounded } from "@mui/icons-material";
import MissingSignatureReasonSelect from "./MissingSignatureReasonSelect";
import {
	MissingSignatureReason,
	getDefaultTextCode,
	resolveDefaultSignatureText,
} from "./missingSignatureReason";

const Signature = () => {
	const theme = useTheme();
	const {t} = useTranslation(['network', 'translation']);
	const navigate = useNavigate();
	const appLanguage = useTranslation();
	const {dispatchGlobalData} = useContext(GlobalContext);
	const raFetchedData = useRecoilValue(raFetchedDataState);
	const isRce: boolean = raFetchedData?.rce ? true : false
	const recoveryMode = useRecoilValue(recoveryRaModeState);

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);
	const [open, setOpen] = useState(false);

	const [signDoc, setSignDoc] = useState<any>(null);
	const [signRefOpr, setSignRefOpr] = useState<SignatureCanvas | null>(null);
	const [signIsPresentOpr, setSignIsPresentOpr] = useState(false);
	const [signRefCus, setSignRefCus] = useState<SignatureCanvas | null>(null);
	const [signIsPresentCus, setSignIsPresentCus] = useState(false);

	// SCFE-1 (LPP-22292): motivazione strutturata di mancata firma (ramo check-in).
	// Selezione esclusiva (VB-06); popola il campo firma col code del testo di default (tradotto sul FE).
	const [missingSignatureReason, setMissingSignatureReason] = useState<MissingSignatureReason | null>(null);

	let confirmFormListErrSt: boolean = false;

	const checkOutInMode = useRecoilValue(checkOutInModeState);
	// ramo check-in (in/subIn): solo qui è ammessa la motivazione di mancata firma
	const isCheckIn = ['in', 'subIn'].includes(checkOutInMode);
	// inserito	=> kms, tank, battery & photos
	const checkVehicle = useRecoilValue(checkVehicleStatusState);
	// check-damage events
	const eventsInternal = useRecoilValue<any>(eventsTypeState('internal'));
	const eventsExternal = useRecoilValue<any>(eventsTypeState('external'));
	// equipment
	const equipmentsEvent = useRecoilValue(equipmentsEventState);
	const checkEquipments = useRecoilValue(checkEquipmentsState);
	// Calculated costs
	const summary = useRecoilValue(summaryState);

	const [raLang, setRaLang] = useState('it');
	const [anchorElLang, setAnchorElLang] = React.useState<null | HTMLElement>(null);
	const openLang = Boolean(anchorElLang);

	const billingCustomerId = useMemo(() => {
		if (['in', 'subIn', 'out'].includes(checkOutInMode)) {
			return getBillingCustomerId(raFetchedData);
		} else {
			return null;
		}
	}, [raFetchedData]);

	useEffect(() => {
		const fetchCustomer = async () => {
			try {
			const response = await getCustomerBaseById(billingCustomerId);
			const customerData = response.data;
	
			// Supponiamo che la lingua sia in customerData.language
			if (customerData.data.language) {
				const langCode = customerData?.data?.language?.toLowerCase();
				//console.log('langCode:', langCode);
				switch (langCode) {
					case 'fr-FR':
					case 'fr':
					//console.log('fr');
					setRaLang('fr');
					break;
					case 'it-IT':
					case 'it':
					//console.log('it');
					setRaLang('it');
					break;
					case 'es-ES':
					case 'es':
					//console.log('es');
					setRaLang('es');
					break;
					case 'pt-PT':
					case 'pt':
					//console.log('pt');
					setRaLang('pt');
					break;
					default:
					//console.log('en');
					setRaLang('en');
					break;
				}
			}else{
				//console.log('default lang');
				setRaLang('en');
			}
			} catch (error) {
			console.error('Errore nel recupero del cliente:', error);
			}
		};

		// Call the fetchCustomer function
		if (billingCustomerId) {
			fetchCustomer();
		}
		
	}, [billingCustomerId]);
	

	const viewErrorMessage = (message: string, type = snackbarTypes.error) => {
		dispatchGlobalData({
			type: setSnackbar,
			payload: {
				snackbar: {
					message: t(message),
					type: type,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	const saveSignaturesMutation = useMutation(
		({ saveSignaturesRequest, language, rce }: { saveSignaturesRequest: any; language: string; rce: boolean }) =>
			fetchRaUpdateCheckInOut({saveSignaturesRequest, language, rce }), // Combina il parametro language con la richiesta
		{
			onSuccess: (data) => {
				viewErrorMessage('raCheckOutIn.signature.message.success.save', snackbarTypes.success);
				clearCheckOutInSessionStorage();
				navigate("/");
			},
			onError: (error) => {
				viewErrorMessage('raCheckOutIn.signature.message.error.save');
			}
		});

	const handleOnUpload = (file: File) => {
		if (!!file) {
			setSignDoc(file);
		}
	}

	const checkOutInDownloadPdf = useMutation(
		raPreViewDownloadPdf,
		{
			onSuccess: async (response: any) => {
				if (!!response) {
					const blob = new Blob([response], { type: 'application/pdf' });
					const pdfURL = URL.createObjectURL(blob);
					const link = document.createElement('a');
					document.body.append(link);
					link.href = pdfURL;
					link.download = `${raFetchedData?.idRentalAgreement}.pdf`;
					link.click();
				}
			},
			onError: (error:any) => {
			    if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_ON_PRC_RECALCULATE_DROP_OFF_DATE\"") {
            	    viewErrorMessage('preAuth.message.error.prcDropOffDate');
            	}else{
    				viewErrorMessage('translation:error.tryLater');
            	}
			}
		});

	const recoveryCheckInUploadDocMutation = useMutation(
		recoveryCheckInUploadDoc,
		{
			onError: (error: any) => {
				viewErrorMessage('translation:error.tryLater');
			}
		});

	const handleOnRemove = () => {
		setSignDoc(null);
	}

	const handleSaveRA = async () => {
		// close confir dialog
		setOpen(false);

		// Prepara il parametro language
		const language = raLang ? raLang : appLanguage.i18n.language;

		if (recoveryMode) {
			const form = new FormData();
			form.append('file', signDoc);

			const uploadCheckInDocReq: any = {
				idRentalAgreement: encodeURIComponent(raFetchedData.idRentalAgreement),
				type: ['in', 'subIn'].includes(checkOutInMode) ? 'CHECK_IN' : 'CHECK_OUT',
				receipt: form
			};

			await recoveryCheckInUploadDocMutation.mutate(uploadCheckInDocReq);
		}

		// confirm all forms
		const intExtEvents = deepClone(eventsExternal.concat(eventsInternal));

		if (checkOutInMode === 'out') {
			const confirmFormList: any[] = [];
			intExtEvents
				.map((event: IEvent) => {
					const eventDmgIds = event.damages.map((dmg: IDamage) => dmg.id);
					confirmFormList.push({
						formId: event.id,
						inspectionKM: checkVehicle.kmsOnCheck,
						inspectionFuelLeve: checkVehicle.tankLevelOnCheck?.code,
						inspectionBatteryLevel: checkVehicle.batteryLevelOnCheck?.code,
						expectedCheckInDate: raFetchedData.bookingDetail?.expectedCheckInDate,
						effectiveCheckInDate: !!checkVehicle.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
						missingDotationAmount: !!summary?.missingDotationAmount ? summary.missingDotationAmount : 0,
						gridAmount: !!summary?.damagePrice?.damagePrices ?
							summary.damagePrice.damagePrices
								.filter((dmg: any) => eventDmgIds.includes(dmg.id))
								.map((dmg: any) => dmg.price)
								.reduce((partialSum: number, next: number) => partialSum + next, 0) :
							0,
						keybox: checkVehicle.keyBoxServiceApplicable && isEffectiveDateChanged(checkVehicle.effectiveDateTime, checkVehicle.now),
						damageOwnerSapid: billingCustomerId
					});
					return event;
				});

			// add equipment event
			if (!!equipmentsEvent.id) {
				confirmFormList.push({
					formId: equipmentsEvent.id,
					inspectionKM: checkVehicle.kmsOnCheck,
					inspectionFuelLevel: checkVehicle.tankLevelOnCheck?.code,
					inspectionBatteryLevel: checkVehicle.batteryLevelOnCheck?.code,
					expectedCheckInDate: raFetchedData.bookingDetail?.pickupDate, //'da ra expectedCheckInDate - expected dropoff Data', o checkIn: expectedCheckInDate
					effectiveCheckInDate: !!checkVehicle.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
					gridAmount: 0,
					missingDotationAmount: !!summary?.missingDotationAmount ? summary.missingDotationAmount : 0,
					damageOwnerSapid: billingCustomerId
				});
			}

			// /* Confirm all Forms */
			if (confirmFormList.length > 0) {
				confirmFormListErrSt = false;
				await confirmFormMutation.mutateAsync({
					tenant: raFetchedData.idTenant,
					plate: raFetchedData.vehicle?.plate?.code,
					forms: {formDataList: confirmFormList}
				} as any);

				if (confirmFormListErrSt) {
					return;
				}
			}
		}

		let saveSignaturesRequest: any = {
			"idRentalAgreement": 	raFetchedData?.idRentalAgreement
		};

		if (!recoveryMode) {
			saveSignaturesRequest['checkoutScan'] = signDoc;
		}

		let dmgPr:any = {};
		switch (checkOutInMode) {
			case 'out':
				saveSignaturesRequest = {
					...saveSignaturesRequest,
					"checkOut": {
						"state": "COMPLETED",
						"operatorDigitSign": signRefOpr?.getSignaturePad().toDataURL('image/png'),
						"customerDigitSign": signRefCus?.getSignaturePad().toDataURL('image/png'),

						effectiveCheckoutDate: 		dayjs().toISOString(),
						kmsOnCheck: 				checkVehicle.kmsOnCheck,
						kmsOnCheckPhoto: 			checkVehicle.kmsOnCheckPhoto,
						tankLevelOnCheck: 			checkVehicle.tankLevelOnCheck?.code,
						tankLevelOnCheckPhoto: 		checkVehicle.tankLevelOnCheckPhoto,
						batteryLevelOnCheck: 		checkVehicle.batteryLevelOnCheck?.code,
						batteryLevelOnCheckPhoto: 	checkVehicle.batteryLevelOnCheckPhoto,
						equipments:
							checkEquipments.equipments
								.map((equip: any) => {
									return {
										idEquipment: 		equip.data.id,
										equipmentPrice: 	equip.data.priceInCents,
										equipment: {
											description: 	equip.data.description,
											label: 			equip.data.description,
											code: 			equip.data.code
										},
										associated: 		equip.data.associated,
										present: 			equip.present,
										imagePresent: 		equip.missing
									}
								}),
						// equipmentComments:
						// 	checkEquipments.comments
						// 		.map((equip: any) => {
						// 			return {
						// 				createDateTime: equip.date,
						// 				notes: equip.comment
						// 			}
						// 		}),
						vehicleDamages: intExtEvents
							.map((evt: any) => evt.damages)
							.flat()
							.map((dmg: any) => {
								dmgPr = summary.damagePrice.damagePrices.filter((dmgPr: any) => dmgPr.damageId === dmg.id)[0];
								return {
									idOprDamage: 		dmg.id,
									damageFormId: 		dmg.damageFormId,
									damagePart: {
										description: 	dmg.part?.descr,
										label: 			dmg.part?.descr,
										code: 			dmg.part?.code
									},
									damagePosition: {
										description: 	dmg.position?.descr,
										label: 			dmg.position?.descr,
										code: 			dmg.position?.code
									},
									damageSeverity: {
										description: 	dmg.severity,
										code: 			dmg.severity
									},
									figureCode: 		dmg.figureCode,
									photoScan: 			dmg.snapshotIdList
															.map((elem: string) => ({
																documentFile: 	elem
															})),
									newDamage: 			true,
									damagePrice: 		!!dmgPr ? dmgPr.price : 0
								}
							})
					}
				}
				break;
			case 'in':
			case 'subIn':
				saveSignaturesRequest = {
					...saveSignaturesRequest,
					bookingDetail: {
						keybox: 					checkVehicle.keyBoxServiceApplicable && isEffectiveDateChanged(checkVehicle.effectiveDateTime, checkVehicle.now),
					},
					"checkIn": {
						"state": "COMPLETED",
						"operatorDigitSign": signRefOpr?.getSignaturePad().toDataURL('image/png'),
						"customerDigitSign": signRefCus?.getSignaturePad().toDataURL('image/png'),

						// SCFE-1 (LPP-22292): motivazione di mancata firma + code/testo di default tradotto.
						// Il BE (Task 3) bypassa la validazione firma e valorizza il campo firma col code.
						missingSignatureReason: missingSignatureReason,
						missingSignatureTextCode: getDefaultTextCode(missingSignatureReason),
						missingSignatureText: resolveDefaultSignatureText(
							missingSignatureReason,
							(code) => t(`raCheckOutIn.signature.missingSignature.defaultText.${code}`, {lng: language, defaultValue: ''}) as string
						),

						extraKmAmount: 				!!summary.raCheckInExtra?.amountResponseExtraKm?.amountTaxIncluded ? summary.raCheckInExtra.amountResponseExtraKm.amountTaxIncluded : 0,
						extraDaysAmount: 			!!summary.raCheckInExtra?.amountResponseExtraDays?.amountTaxIncluded ? summary.raCheckInExtra.amountResponseExtraDays.amountTaxIncluded : 0,
						extraAncillaryAmount: 		!!summary.raCheckInExtra?.amountResponseExtraDays?.extraAncillaryAmount ? summary.raCheckInExtra.amountResponseExtraDays.extraAncillaryAmount : 0,
						termicRefilAmount: 			!!summary.fuelExtra ? summary.fuelExtra * 100 : 0,
						refuelServiceAmount: 		!!summary.refuelServiceAmount ? summary.refuelServiceAmount.replace(',', '.')*100 : 0,
						extraTermicLitres:			!!summary.termicFuelPrice ? summary.extraTermicLitres : !!summary.fuelExtra ? 1 : summary.extraTermicLitres,
						termicFuelPrice: 			!!summary.termicFuelPrice ? summary.termicFuelPrice : !!summary.fuelExtra ? summary.fuelExtra * 1000 : 0,
						socRefilAmount: 			!!summary.batteryExtra ? summary.batteryExtra * 100 : 0,
						extraSocKws:				summary.extraSocKws,
						extraSocKwsPrice: 			summary.extraSocKwsPrice,
						// LPP-22945 (feedback INT): extra costi manuali lavaggio/recupero (centesimi) + foto/descrizione.
						extraWashAmount: 			!!summary.washExtra ? Math.round(parseFloat(String(summary.washExtra).replace(',', '.')) * 100) : 0,
						extraWashDescription: 		summary.washDescription ?? null,
						extraWashPhoto: 			summary.washPhoto ?? null,
						extraRecoveryAmount: 		!!summary.recoveryExtra ? Math.round(parseFloat(String(summary.recoveryExtra).replace(',', '.')) * 100) : 0,
						extraRecoveryDescription: 	summary.recoveryDescription ?? null,
						extraRecoveryPhoto: 		summary.recoveryPhoto ?? null,
						effectiveCheckInDate: 		!!checkVehicle.effectiveDateTime ? checkVehicle.effectiveDateTime.toISOString() : null,
						kmsOnCheck: 				checkVehicle.kmsOnCheck,
						kmsOnCheckPhoto: 			checkVehicle.kmsOnCheckPhoto,
						tankLevelOnCheck: 			checkVehicle.tankLevelOnCheck?.code,
						tankLevelOnCheckPhoto: 		checkVehicle.tankLevelOnCheckPhoto,
						batteryLevelOnCheck: 		checkVehicle.batteryLevelOnCheck?.code,
						batteryLevelOnCheckPhoto: 	checkVehicle.batteryLevelOnCheckPhoto,
						dmsIdentificationCode: 		checkVehicle.dropOffLocation,
						vehicleNotRunning: 			checkVehicle.nonRunningVehicle,
						equipments:
							checkEquipments.equipments
								.map((equip: any) => {
									return {
										idEquipment: 		equip.data.id,
										equipmentPrice: 	equip.data.priceInCents,
										equipment: {
											description: 	equip.data.description,
											label: 			equip.data.description,
											code: 			equip.data.code
										},
										associated: 		equip.data.associated,
										present: 			equip.present,
										imagePresent: 		equip.missing
									}
								}),
						// equipmentComments:
						// 	checkEquipments.comments
						// 		.map((equip: any) => {
						// 			return {
						// 				createDateTime: equip.date,
						// 				notes: equip.comment
						// 			}
						// 		}),
						vehicleDamages: intExtEvents
							.map((evt: any) => evt.damages)
							.flat()
							.map((dmg: any) => {
								dmgPr = summary.damagePrice.damagePrices.filter((dmgPr: any) => dmgPr.damageId === dmg.id)[0];
								return {
									idOprDamage: 		dmg.id,
									damageFormId: 		dmg.damageFormId,
									damagePart: {
										description: 	dmg.part?.descr,
										label: 			dmg.part?.descr,
										code: 			dmg.part?.code
									},
									damagePosition: {
										description: 	dmg.position?.descr,
										label: 			dmg.position?.descr,
										code: 			dmg.position?.code
									},
									damageSeverity: {
										description: 	dmg.severity,
										code: 			dmg.severity
									},
									figureCode: 		dmg.figureCode,
									photoScan: 			dmg.snapshotIdList
															.map((elem: string) => ({
																documentFile: elem
															})),
									newDamage: 			true,
									damagePrice: 		!!dmgPr ? dmgPr.price : 0
								}
							}),

						missingParts: equipmentsEvent.id ?? null
					}
				}
				break;
		}
        //console.log('saveSignaturesRequest ', saveSignaturesRequest);
		//console.log('language:', language);
		const rce = isRce;
		saveSignaturesMutation.mutate({ saveSignaturesRequest, language, rce });
	}

	// /** Download RA to sign */
	// const handleDownloadRA = () => {
	// 	checkOutInDownloadPdf.mutate({
	// 		pdfType: checkOutInMode === 'out' ? DOWNLOAD_PDF_TYPE.CHECKOUT : DOWNLOAD_PDF_TYPE.CHECKIN,
	// 		idRentalAgreement: raFetchedData?.idRentalAgreement,
	// 		language: appLanguage.i18n.language
	// 	} as any);
	// }

	// /** Confirm Form Mutation */
	const confirmFormMutation = useMutation(
		fetchCheckOutInAsyncConfirmForm,
		{
			onSuccess: (response: any) => {
				//if (!response?.[0]) {
				//	viewErrorMessage('translation:error.tryLater');
				//}
			},
			onError: (error) => {
				confirmFormListErrSt = true;
				viewErrorMessage('translation:error.tryLater');
			}
		});
	
	const handleClickLang = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElLang(event.currentTarget);
	};

	const handleCloseLang = () => {
        setAnchorElLang(null);
    };

    const handleChangeLang = (lang: string) => {
        setRaLang(lang);
        handleCloseLang();
    }

	const renderSelectedLang = () => {
		switch (raLang) {
			case 'fr':
			case 'fr-FR':
				return (<FR style={{borderRadius: '50%'}} title={'Francese'} width={20} height={20}/>)
			case 'it':
			case 'it-IT':
				return (<IT style={{borderRadius: '50%'}} title={'Italiano'} width={20} height={20}/>)
			case 'es':
			case 'es-ES':
				return (<ES style={{borderRadius: '50%'}} title={'Spagnolo'} width={20} height={20}/>)
			case 'pt':
			case 'pt-PT':
				return (<PT style={{borderRadius: '50%'}} title={'Português'} width={20} height={20}/>)
			case 'en':
			default:
				return (<GB style={{borderRadius: '50%'}} title={'English'} width={20} height={20}/>)
		}
	}

	return (
		<Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
			{
				recoveryMode &&
				<Grid container columnSpacing={2}>
					{/*<Grid item xs={12} md={6}*/}
					{/*	  sx={{display: "flex", justifyContent: "center", alignItems: 'center'}}>*/}
					{/*	<Button color={"primary"}*/}
					{/*			onClick={handleDownloadRA}>{t('raCheckOutIn.signature.button.download')}</Button>*/}
					{/*</Grid>*/}

					<Grid item xs={12}>
						<Typography variant={"bodyLarge"}>
							{t("raCheckOutIn.signature.label.upload")}
						</Typography>

						<FileUploader onUpload={(file: File) => handleOnUpload(file)}
									  onRemove={() => handleOnRemove()}	/>
					</Grid>

				</Grid>
			}

			{
				!recoveryMode &&
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<SignStep step={{"step-key": "operatorSign", "step-url": "/signature"}}
										  signRef={signRefOpr} setSignRef={setSignRefOpr} setSignIsPresent={setSignIsPresentOpr} />
							</Grid>
							<Grid item>
								<Button variant="outlined" onClick={() => {
									signRefOpr?.clear();
									setSignIsPresentOpr(false);
								}}>{t('digitalSign.label.clear')}</Button>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						{/* LPP-21950 (SC5): con un motivo di mancata firma la firma CLIENTE è derogata -> campo disabilitato. */}
						<Box sx={(isCheckIn && !!missingSignatureReason) ? {pointerEvents: 'none', opacity: 0.5} : undefined}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<SignStep step={{"step-key": "customerSign", "step-url": "/signature"}}
											  signRef={signRefCus} setSignRef={setSignRefCus} setSignIsPresent={setSignIsPresentCus} />
								</Grid>
								<Grid item xs={12}>
									<Button variant="outlined" onClick={() => {
										signRefCus?.clear();
										setSignIsPresentCus(false);
									}}>{t('digitalSign.label.clear')}</Button>
								</Grid>
							</Grid>
						</Box>
					</Grid>
				</Grid>
			}

			{
				!recoveryMode && isCheckIn &&
				<Box sx={{mt: theme.spacing(2)}}>
					<MissingSignatureReasonSelect
						value={missingSignatureReason}
						onChange={(reason) => {
							setMissingSignatureReason(reason);
							// LPP-21950 (SC5): selezionato un motivo, la firma CLIENTE è derogata -> azzerala
							// (il campo viene anche disabilitato). La firma OPERATORE resta obbligatoria.
							if (reason) {
								signRefCus?.clear();
								setSignIsPresentCus(false);
							}
						}}
						language={raLang}
					/>
				</Box>
			}

			<CheckStepper disableBack={true}/>
			
			<Box sx={{display: "flex", justifyContent: "center", mt: theme.spacing(2)}}>
				<Typography>{t('preview.form.label.raLang')}</Typography>
				<Box sx={{mr: theme.spacing(2)}}>
					<IconButton
						onClick={handleClickLang}
						aria-controls={openLang ? 'account-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={openLang ? 'true' : undefined}
					>
						{renderSelectedLang()}
					</IconButton>
				</Box>
				<Menu
					anchorEl={anchorElLang}
					id="account-menu"
					open={openLang}
					onClose={handleCloseLang}
					onClick={handleCloseLang}
					transformOrigin={{horizontal: 'right', vertical: 'top'}}
					anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
				>
					<MenuItem onClick={() => {handleChangeLang('en')}}>
						<GB style={{borderRadius: '50%'}} title={'English'} width={20} height={20}/>
					</MenuItem>

					<MenuItem onClick={() => {handleChangeLang('it')}}>
						<IT style={{borderRadius: '50%'}} title={'Italiano'} width={20} height={20}/>
					</MenuItem>

					<MenuItem onClick={() => {handleChangeLang('fr')}}>
						<FR style={{borderRadius: '50%'}} title={'Francese'} width={20} height={20}/>
					</MenuItem>

					<MenuItem onClick={() => {handleChangeLang('es')}}>
						<ES style={{borderRadius: '50%'}} title={'Español'} width={20} height={20}/>
					</MenuItem>

					<MenuItem onClick={() => {handleChangeLang('pt')}}>
						<PT style={{borderRadius: '50%'}} title={'Português'} width={20} height={20}/>
					</MenuItem>
				</Menu>
				{/* LPP-21950 (SC5): un motivo di mancata firma deroga la SOLA firma cliente; la firma OPERATORE
				    resta obbligatoria (prima bastava il motivo e si concludeva senza alcuna firma). */}
				<Button sx={{mx: theme.spacing(1)}}
				        disabled={!(!!signDoc || (signIsPresentOpr && signIsPresentCus) || (isCheckIn && !!missingSignatureReason && signIsPresentOpr))}
						color={"accent"}
						onClick={handleOpen}>
					{t('raCheckOutIn.signature.button.continues')}
				</Button>
			</Box>
				
			
			<CustomLoader loading={recoveryCheckInUploadDocMutation.isLoading || saveSignaturesMutation.isLoading || checkOutInDownloadPdf.isLoading || confirmFormMutation.isLoading} />

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">
					{t("raCheckOutIn.signature.message.dialogConfirmTitle")}
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="alert-dialog-description">
						{t("raCheckOutIn.signature.message.dialogConfirmContent")}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant={"outlined"} color={'secondary'} onClick={handleClose}>{t('digitalSign.label.cancel')}</Button>
					<Button onClick={handleSaveRA}>{t('digitalSign.label.confirm')}</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
}

export default Signature;